import { createRootRoute, createRoute, createRouter } from '@tanstack/react-router'
import { useEffect } from 'react'
import { AppShell } from './components/AppShell'
import { Ablage } from './routes/Ablage'
import { AlleAufgaben } from './routes/AlleAufgaben'
import { Beteiligt } from './routes/Beteiligt'
import { ClaudeFreigabe } from './routes/ClaudeFreigabe'
import { Leitstand } from './routes/Leitstand'
import { MeinTag } from './routes/MeinTag'
import { MyWork } from './routes/MyWork'
import { NotizbuchSeite } from './notizbuch'
import { Einstellungen } from './routes/Einstellungen'
import { Eingang } from './routes/Eingang'
import { Melden } from './routes/Melden'
import { NetconRueckweg } from './routes/NetconRueckweg'
import { Planung } from './routes/Planung'
import { PlanEntwuerfe, PlanEntwurf } from './routes/PlanEntwurf'
import { Vorhaben } from './routes/Vorhaben'
import { Pools } from './routes/Pools'

/**
 * Routen im Code statt über Dateikonventionen – bei neun festen Ansichten
 * (Konzept Kap. 5) ist das übersichtlicher und braucht keinen Generierungsschritt.
 * Die Pfade stehen in lib/navigation.ts, damit Seitenleiste, Router und
 * ⌘K-Palette dieselbe Quelle nutzen.
 */
const rootRoute = createRootRoute({ component: AppShell })

const routen = [
  { path: '/', component: MeinTag },
  /** My Work (Z-24, E-453) – zusätzlich zu Mein Tag, von Nils beauftragt. */
  { path: '/my-work', component: MyWork },
  { path: '/pools', component: Pools },
  { path: '/planung', component: Planung },
  { path: '/eingang', component: Eingang },
  { path: '/alle', component: AlleAufgaben },
  { path: '/leitstand', component: Leitstand },
  { path: '/vorhaben', component: Vorhaben },
  /** Die Ablage – Dokumente je Arbeitswelt (Z-14, 11.09.2026). Ein zehnter Punkt, von Nils beauftragt. */
  { path: '/ablage', component: Ablage },
  /**
   * **Mein Bereich ist die Sicht des Fachbereichs** (`v-bereich`) – und damit der Ort für das
   * Melde-Formular (Phase 6). Ein zehnter Eintrag in der Seitenleiste wäre eine Änderung an der
   * UI-Referenz, und die gibt es nur nach Absprache (CLAUDE.md).
   */
  { path: '/bereich', component: Melden },
  /**
   * **Die Ansicht eines Gastes** (18.08.2026, E-130). Sie steht nur in seiner Seitenleiste;
   * für alle anderen Rollen bleiben es die neun Ansichten des Mockups.
   */
  { path: '/beteiligt', component: Beteiligt },
  { path: '/einstellungen', component: Einstellungen },
  /**
   * **Der Rückweg von NetCon.Cloud** (01.09.2026,
   * [E-289](../../../docs/ENTSCHEIDUNGEN.md#e-289)).
   *
   * Keine Ansicht im Sinne des Konzepts und deshalb auch nicht in `navigation.ts`: sie steht in
   * keiner Seitenleiste und wird nie angeklickt, sondern nur von Keycloak angesteuert. Der Pfad
   * muss auf das Zeichen genau dem entsprechen, was im Realm als Rückweg eingetragen ist.
   */
  { path: '/auth/netcon/callback', component: NetconRueckweg },
  /**
   * **Die Freigabe beim Verbinden von Claude** (Z-21). Wie der NetCon-Rückweg keine Ansicht:
   * angesteuert nur von claude.ai oder Claude Code, als `authorization_endpoint` der
   * OAuth-Metadaten (`apps/api/src/claude/oauth.ts`).
   */
  { path: '/claude/freigeben', component: ClaudeFreigabe },
  /**
   * **Das Notizbuch** (Z-23, E-419) – eigener Baustein, erst beim Öffnen geladen, damit sein
   * Code nicht im Bündel der übrigen Ansichten landet. In Stufe 0 ohne Eintrag in der Seitenleiste.
   */
  { path: '/notizbuch', component: NotizbuchSeite },
  { path: '/entwuerfe', component: PlanEntwuerfe },
  { path: '/entwurf/$id', component: PlanEntwurf },
].map(({ path, component }) => createRoute({ getParentRoute: () => rootRoute, path, component }))

const routeTree = rootRoute.addChildren(routen)

/**
 * **Wenn eine Ansicht abstürzt** (23.10.2026): statt „Something went wrong!" eine deutsche Seite mit
 * „Neu laden" – und der Fehler geht ans Server-Log (`/api/browserfehler`), damit man ihn sieht,
 * ohne dass jemand ein Bildschirmfoto schicken muss.
 */
function Absturz({ error }: { error: unknown }) {
  const f = error instanceof Error ? error : new Error(String(error))
  useEffect(() => {
    try {
      navigator.sendBeacon?.(
        '/api/browserfehler',
        JSON.stringify({
          meldung: f.message,
          stapel: f.stack ?? '',
          adresse: location.pathname + location.hash,
        }),
      )
    } catch {
      // Melden ist eine Zugabe
    }
  }, [f])
  return (
    <div className="mx-auto max-w-[560px] px-4 py-10 text-[14px]">
      <h1 className="text-[20px] font-bold">Da ist etwas schiefgegangen.</h1>
      <p className="mt-2 text-ink2">
        Der Fehler ist gemeldet. Meist hilft Neuladen; deine Notizen sind gespeichert.
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => location.reload()}
          className="cursor-pointer rounded-lg bg-accent px-3 py-2 font-semibold text-white"
        >
          Neu laden
        </button>
        <a href="/" className="rounded-lg border border-line2 px-3 py-2 font-semibold text-ink2">
          Zu Mein Tag
        </a>
      </div>
      <details className="mt-4 text-[12px] text-muted">
        <summary className="cursor-pointer">Fehlermeldung</summary>
        <pre className="mt-1 whitespace-pre-wrap">{f.message}</pre>
      </details>
    </div>
  )
}

export const router = createRouter({ routeTree, defaultErrorComponent: Absturz })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
